import { inject, Injectable } from '@angular/core'
import { ChantierState } from '../data-access/state/chantier.state'
import { ChantierDTO } from '../data-access/http/dto/chantier-dto'
import { UserState } from '../data-access/state/user.state'
import {
    combineLatestWith,
    distinctUntilChanged,
    filter,
    map,
    mergeWith,
    Observable,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs'
import { ChantierHttpService } from '../data-access/http/chantier-http.service'
import { DOCUMENT, Location } from '@angular/common'
import { ContactCsm } from '../models/contact-csm'
import { ParamDomainService } from './param.domain'
import { ParamCodeEnum } from '../models/enums/param-code.enum'
//@ts-expect-error This library doesn't include ts types, so we bypass TS in order to use it
import HtmlSanitizer from 'jitbit-html-sanitizer'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { isInThePast, parseCustomDate } from '../utils/date.utils'
import { NavigationEnd, Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr'

@Injectable({ providedIn: 'root' })
export class ChantierDomainService {
    private readonly chantierState = inject(ChantierState)
    private readonly location = inject(Location)
    private readonly chantierHttpService = inject(ChantierHttpService)
    private readonly paramDomainService = inject(ParamDomainService)
    private readonly sanitizer = inject(DomSanitizer)
    private readonly document = inject(DOCUMENT)
    private readonly toastr = inject(ToastrService)
    private router = inject(Router)

    private userState = inject(UserState)

    // Selectors
    selectedChantier$: Observable<ChantierDTO> = this.chantierState.store
        .select(ChantierState.getSelectedChantier)
        .pipe(
            distinctUntilChanged(
                (chantier1, chantier2) =>
                    chantier1 &&
                    chantier2 &&
                    chantier1.code === chantier2.code &&
                    chantier1.auteurPhotoChantier === chantier2.auteurPhotoChantier,
            ),
            map((selectedChantier) => {
                if (selectedChantier) {
                    return selectedChantier
                }
                return this.userState.store
                    .selectSnapshot(UserState.getUserChantiers)
                    .find(
                        (chantier) =>
                            chantier.code === this.userState.store.selectSnapshot(UserState.getLastChantierCode),
                    )
            }),
        )
    selectedChantierCode$: Observable<string> = this.chantierState.store
        .select(ChantierState.getSelectedChantierCode)
        .pipe(
            map((selectedChantierCode) => {
                if (selectedChantierCode) {
                    return selectedChantierCode
                }
                return this.userState.store.selectSnapshot(UserState.getLastChantierCode)
            }),
        )

    selectedChantierPhotoUrl$ = this.chantierState.store.select(ChantierState.getSelectedChantierPhotoUrl)
    selectableChantiers$ = this.userState.store.select(UserState.getUserChantiers).pipe(
        combineLatestWith(this.chantierState.store.select(ChantierState.getSelectedChantier)),
        map(([chantiers, selectedChantier]) =>
            chantiers.filter((chantier) => chantier.code !== selectedChantier?.code),
        ),
    )
    selectedChantierAdmins$ = this.chantierState.store.select(ChantierState.getSelectedChantierAdmins)
    selectedChantierCSM$: Observable<ContactCsm> = this.chantierState.store.select(ChantierState.getSelectedChantierCSM)
    shouldDisplayContactCSM$ = this.selectedChantierCSM$.pipe(
        combineLatestWith(
            this.router.events.pipe(
                filter((event) => event instanceof NavigationEnd),
                mergeWith(of(true)),
                map(() => this.router.url.split('/')[1] === 'chantiers'),
            ),
        ),
        map(([contactCsm, userIsOnHomepage]) => userIsOnHomepage && !!contactCsm?.nom && !!contactCsm?.telephone),
    )
    userIsAdminChantier$: Observable<boolean> = this.chantierState.store
        .select(ChantierState.getSelectedChantier)
        .pipe(map((selectedChantier) => selectedChantier.admin))
    selectedChantierIsIris$: Observable<boolean> = this.chantierState.store
        .select(ChantierState.getSelectedChantier)
        .pipe(map((selectedChantier) => selectedChantier?.iris))
    actualiteChantiers$: Observable<SafeHtml> = this.selectedChantierIsIris$.pipe(
        map((isIris) => {
            if (isIris) {
                return this.paramDomainService.getParamByCode(ParamCodeEnum.globalMessageIris)?.valeurParam
            }
            return this.paramDomainService.getParamByCode(ParamCodeEnum.globalMessage)?.valeurParam
        }),
        map((actualiteChantiers) =>
            this.sanitizer.bypassSecurityTrustHtml(new HtmlSanitizer({}).sanitizeHtml(actualiteChantiers || '')),
        ),
    )
    actualitesAreExpired$: Observable<boolean> = this.selectedChantierIsIris$.pipe(
        map((isIris) => {
            if (isIris) {
                const dateExpirationIris = this.paramDomainService.getParamByCode(
                    ParamCodeEnum.dateValiditeActualiteIris,
                )?.valeurParam
                return isInThePast(parseCustomDate(dateExpirationIris))
            }
            const dateExpirationChantier = this.paramDomainService.getParamByCode(
                ParamCodeEnum.dateValiditeActualite,
            )?.valeurParam
            return isInThePast(parseCustomDate(dateExpirationChantier))
        }),
    )
    supportEmail$: Observable<string> = this.paramDomainService.params$.pipe(
        combineLatestWith(this.chantierState.store.select(ChantierState.getSelectedChantier)),
        map(([params, selectedChantier]) => {
            const paramCode: ParamCodeEnum = selectedChantier?.iris
                ? ParamCodeEnum.footerContactMailIris
                : ParamCodeEnum.footerContactMail
            return params?.find((param) => param.codeParam === paramCode)?.valeurParam
        }),
    )
    selectedChantierCode(): string {
        return (
            this.chantierState.store.selectSnapshot(ChantierState.getSelectedChantierCode) ||
            this.userState.store.selectSnapshot(UserState.getLastChantierCode)
        )
    }

    chantierIsSelectable(chantierCode: string): boolean {
        return this.userState.store
            .selectSnapshot(UserState.getUserChantiers)
            .some((chantier) => chantier.code === chantierCode)
    }

    chantierIsSelected(chantierCode: string): boolean {
        return this.chantierState.store.selectSnapshot(ChantierState.getSelectedChantierCode) === chantierCode
    }

    supportEmail(): string {
        const selectedChantierIsIris = this.chantierState.store.selectSnapshot(ChantierState.getSelectedChantier)?.iris
        return selectedChantierIsIris
            ? this.paramDomainService.getParamByCode(ParamCodeEnum.footerContactMailIris)?.valeurParam
            : this.paramDomainService.getParamByCode(ParamCodeEnum.footerContactMail)?.valeurParam
    }

    // Actions
    setSelectedChantier(chantierCode: string): void {
        const userChantiers = this.userState.store.selectSnapshot(UserState.getUserChantiers)
        const selectedChantier = userChantiers.find((chantier) => chantier.code === chantierCode) || userChantiers[0]
        if (!selectedChantier) {
            this.router.navigate(['/no-chantier'])
        }
        this.chantierState.store.update((state) => ({ ...state, selectedChantier }))
        if (!this.router.url.includes('/interim')) {
            this.location.replaceState('/chantiers/' + selectedChantier.code)
        }
        this.loadPhotoSelectedChantier().subscribe()
    }

    getChantierByCode$(code: string): Observable<ChantierDTO | null> {
        return this.userState.store
            .select(UserState.getUserChantiers)
            .pipe(map((chantiers) => chantiers?.find((c) => c.code === code) ?? null))
    }

    loadPhotoSelectedChantier(): Observable<ChantierDTO> {
        const selectedChantierCode = this.chantierState.store.selectSnapshot(ChantierState.getSelectedChantierCode)
        return this.chantierHttpService.getChantierWithPhoto(selectedChantierCode).pipe(
            tap((chantier) =>
                this.chantierState.store.update((state) => ({
                    ...state,
                    selectedChantier: {
                        ...state.selectedChantier,
                        urlPhotoChantier: chantier.urlPhotoChantier,
                        auteurPhotoChantier: chantier.auteurPhotoChantier,
                    },
                })),
            ),
        )
    }

    addPhotoToSelectedChantier(fileName: string, lambdaId: string): Observable<ChantierDTO> {
        const selectedChantierCode = this.chantierState.store.selectSnapshot(ChantierState.getSelectedChantierCode)
        return this.chantierHttpService
            .setChantierPhoto({ codeChantier: selectedChantierCode, fileName, lambdaId })
            .pipe(switchMap(() => this.loadPhotoSelectedChantier()))
    }

    deletePhotoSelectedChantier(): Observable<ChantierDTO> {
        const selectedChantierCode = this.chantierState.store.selectSnapshot(ChantierState.getSelectedChantierCode)
        return this.chantierHttpService
            .deleteChantierPhoto(selectedChantierCode)
            .pipe(switchMap(() => this.loadPhotoSelectedChantier()))
    }

    copyChantierToClipboard(): void {
        const selectedChantier = this.chantierState.store.selectSnapshot(ChantierState.getSelectedChantier)
        this.document.defaultView.navigator.clipboard.writeText(`${selectedChantier.code} - ${selectedChantier.nom}`)
        this.toastr.success('Chantier copié dans le presse-papier')
    }

    preselectChantierFromUrl(chantierCode: string): Observable<void> {
        return this.userState.store.select(UserState.getUserChantiers).pipe(
            filter((chantiers) => chantiers && chantiers.length > 0),
            take(1),
            switchMap((chantiers) => {
                const selectedChantier = chantiers.find((chantier) => chantier.code === chantierCode)
                if (!selectedChantier) {
                    this.toastr.error(
                        `Le chantier ${chantierCode} n'est pas affecté à votre utilisateur. Contactez votre administrateur.`,
                    )
                    return of(undefined)
                }
                this.chantierState.store.update((state) => ({ ...state, selectedChantier }))
                return this.loadPhotoSelectedChantier().pipe(map((): void => undefined))
            }),
        )
    }
}
